import { useCallback, useEffect, useState } from "react";
import { fetchWithAuth } from "@/lib/api/client";

/**
 * Asignación de proveedor/modelo por rol del flujo de propuestas
 * (writer/critic/editor).
 *
 * Usar un modelo distinto para el crítico que para el escritor evita puntos
 * ciegos correlacionados (el modelo que escribe no es buen juez de sus
 * propios errores). La asignación es una preferencia: si el proveedor del
 * rol falla en runtime, el backend hace failover a la cadena por defecto.
 */

export interface RoleAssignment {
  role: string;
  provider_id: string | null;
  provider_display_name: string | null;
  provider_name: string | null;
  provider_is_active: boolean | null;
  default_model: string | null;
  model_override: string | null;
}

export interface RoleProviderOption {
  id: string;
  display_name: string;
  default_model: string;
  is_active: boolean;
}

const ROLE_LABELS: Record<string, { title: string; description: string }> = {
  writer: {
    title: "Escritor",
    description: "Redacta el borrador de cada sección (generar, regenerar, resumir).",
  },
  critic: {
    title: "Crítico",
    description:
      "Evalúa el borrador contra el pliego. Se recomienda un modelo DISTINTO al del escritor para evitar puntos ciegos compartidos.",
  },
  editor: {
    title: "Editor",
    description:
      "Refina el texto aplicando el feedback (loop crítico y “aplicar sugerencias”). Suele convenir el mismo modelo que el escritor para conservar la voz del texto.",
  },
};

interface RowState {
  providerId: string; // "" = cadena por defecto
  modelOverride: string;
}

interface LLMRoleAssignmentsProps {
  providers: RoleProviderOption[];
}

export function LLMRoleAssignments({ providers }: LLMRoleAssignmentsProps) {
  const [assignments, setAssignments] = useState<RoleAssignment[]>([]);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadAssignments = useCallback(async () => {
    try {
      setError(null);
      const response: { data: RoleAssignment[] } = await fetchWithAuth("/api/v1/llm/roles");
      setAssignments(response.data);
      const next: Record<string, RowState> = {};
      for (const item of response.data) {
        next[item.role] = {
          providerId: item.provider_id ?? "",
          modelOverride: item.model_override ?? "",
        };
      }
      setRows(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar los roles");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const isDirty = (assignment: RoleAssignment): boolean => {
    const row = rows[assignment.role];
    if (!row) return false;
    return (
      row.providerId !== (assignment.provider_id ?? "") ||
      row.modelOverride.trim() !== (assignment.model_override ?? "")
    );
  };

  const handleSave = async (role: string) => {
    const row = rows[role];
    if (!row) return;
    try {
      setSavingRole(role);
      setError(null);
      if (row.providerId === "") {
        await fetchWithAuth(`/api/v1/llm/roles/${role}`, { method: "DELETE" });
      } else {
        await fetchWithAuth(`/api/v1/llm/roles/${role}`, {
          method: "PUT",
          body: JSON.stringify({
            provider_id: row.providerId,
            model_override: row.modelOverride.trim() || null,
          }),
        });
      }
      await loadAssignments();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar la asignación");
    } finally {
      setSavingRole(null);
    }
  };

  const activeProviders = providers.filter((p) => p.is_active);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
        Cargando roles...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200 text-red-700 text-sm">{error}</div>
      )}
      {assignments.map((assignment) => {
        const labels = ROLE_LABELS[assignment.role] ?? {
          title: assignment.role,
          description: "",
        };
        const row = rows[assignment.role] ?? { providerId: "", modelOverride: "" };
        const selectedProvider = activeProviders.find((p) => p.id === row.providerId);
        return (
          <div key={assignment.role} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="md:w-1/3">
              <h3 className="text-sm font-semibold text-gray-800">{labels.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{labels.description}</p>
            </div>
            <div className="flex-1 flex flex-col sm:flex-row gap-2">
              <select
                aria-label={`Proveedor para ${labels.title}`}
                value={row.providerId}
                onChange={(e) =>
                  setRows((prev) => ({
                    ...prev,
                    [assignment.role]: { ...row, providerId: e.target.value },
                  }))
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Por defecto (con failover)</option>
                {activeProviders.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.display_name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                aria-label={`Modelo para ${labels.title}`}
                value={row.modelOverride}
                disabled={row.providerId === ""}
                placeholder={
                  selectedProvider ? selectedProvider.default_model : "modelo del proveedor"
                }
                onChange={(e) =>
                  setRows((prev) => ({
                    ...prev,
                    [assignment.role]: { ...row, modelOverride: e.target.value },
                  }))
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              />
              <button
                onClick={() => handleSave(assignment.role)}
                disabled={!isDirty(assignment) || savingRole === assignment.role}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingRole === assignment.role ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
